import React, { FC, useEffect, useState } from "react";
import { Field, Form } from "react-final-form";
import { Grid, TextField, Button, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import AddBlogService from "../../api/Blogs/AddBlog/addBlogService";
import UpdateBlogService from "../../api/Blogs/EditBlog/editBlogService";
import GetBlogService from "../../api/Blogs/GetBlogs/GetBlogService";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast, { Toaster } from "react-hot-toast";
import Swal from "sweetalert2";
import { imagePath } from "../../context/constant";

const BlogForm: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [editorValue, setEditorValue] = useState("");
  const [initialValues, setInitialValues] = useState<any>({
    title: "",
    author: "",
    tags: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      GetBlogService(id)
        .then((data) => {
          setInitialValues({
            title: data.title,
            author: data.author,
            tags: data.tags.join(", "),
        
          });
          setEditorValue(data.content);
          setThumbnail(data.thumbnail); 
        })
        .catch((err) => {
          toast.error("Failed to load blog for editing");
          console.error(err);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id]);

  const onSubmit = async (values: any) => {
    const formDataToSubmit = new FormData();
    formDataToSubmit.append("title", values.title);
    formDataToSubmit.append("content", editorValue);
    formDataToSubmit.append("author", values.author);
    formDataToSubmit.append(
      "tags",
      values.tags.split(",").map((tag: string) => tag.trim()).join(",")
    );
    
    formDataToSubmit.append("isPublished", "true");

    if (thumbnail) {
      formDataToSubmit.append("thumbnail", thumbnail);
    }

    try {
      if (id) {
        const result = await Swal.fire({
          title: "Are you sure?",
          text: "Do you want to update this blog?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, update it!"
        });

        if (result.isConfirmed) {
          await UpdateBlogService(id, formDataToSubmit);
          toast.success("Blog updated successfully!");
          navigate("/blogs");
        }
      } else {
        await AddBlogService(formDataToSubmit);
        toast.success("Blog added successfully!");
        navigate("/blogs");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save blog. Please try again.");
      console.error(err);
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="p-5 bg-gray-50 rounded-md shadow-sm flex flex-col justify-between"
         style={{ maxWidth: "900px", margin: "10px auto 0 70px", height: "auto" }}>
      <div>
        <Typography variant="h4" className="mb-3" style={{ color: "orange" }}>
          {id ? "Edit Blog" : "Add New Blog"}
        </Typography>
        <Form
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Field name="title">
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        label="Title"
                        variant="outlined"
                        fullWidth
                        size="medium"
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                        required
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name="author">
                    {({ input, meta }) => (
                      <TextField
                        {...input}
                        label="Author"
                        variant="outlined"
                        fullWidth
                        size="medium"
                        error={meta.touched && Boolean(meta.error)}
                        helperText={meta.touched && meta.error}
                        required
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field name="tags">
                    {({ input }) => (
                      <TextField
                        {...input}
                        label="Tags (comma-separated)"
                        variant="outlined"
                        fullWidth
                        size="medium"
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  {thumbnail && (
                    <img
                      src={typeof thumbnail === "string" ? imagePath + thumbnail : URL.createObjectURL(thumbnail)}
                      alt="Thumbnail Preview"
                      style={{
                        width: "100%",
                        height: "150px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        marginBottom: "0.5rem",
                      }}
                    />
                  )}
                  <TextField
                    type="file"
                    name="thumbnail"
                    onChange={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.files && target.files[0]) {
                        setThumbnail(target.files[0]); // Update the thumbnail state with the selected file
                      }
                    }}
                    variant="outlined"
                    fullWidth
                    size="medium"
                    inputProps={{ accept: "image/*" }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <label className="font-semibold">Content</label>
                  <ReactQuill
                    theme="snow"
                    value={editorValue}
                    onChange={setEditorValue}
                    style={{ height: "100px" }}
                  />
                </Grid>
                <Grid item xs={12} mt={10}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    className="h-12 bg-addButton text-white text-sm rounded-sm"
                    disabled={submitting}
                  >
                    {id ? "Update Blog" : "Submit"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        />
      </div>
      <Toaster position="bottom-center" reverseOrder={false} />
    </div>
  );
};

export default BlogForm;
